import type { Dispatch, SetStateAction } from 'react'
import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'

import type { DropdownOption } from '../../dropdown/types/dropdown.types'

export type EditableDropdownOption = Pick<
  DropdownOption,
  'display_value' | 'internal_value' | 'prefix' | 'description' | 'sort_order' | 'is_active'
>

export type NewOptionForm = {
  display_value: string
  internal_value: string
  prefix: string
  description: string
  sort_order: string
  is_active: boolean
}

type DropdownOptionFormsProps = {
  editForm: EditableDropdownOption | null
  editItem: DropdownOption | null
  newForm: NewOptionForm
  onClearNewForm: () => void
  onCreate: () => void
  onDiscardEdit: () => void
  onUpdate: () => void
  setEditForm: Dispatch<SetStateAction<EditableDropdownOption | null>>
  setNewForm: Dispatch<SetStateAction<NewOptionForm>>
  selectedField: string
}

function buildInternalValue(displayValue: string) {
  return displayValue.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function DropdownOptionForms({
  editForm,
  editItem,
  newForm,
  onClearNewForm,
  onCreate,
  onDiscardEdit,
  onUpdate,
  setEditForm,
  setNewForm,
  selectedField,
}: DropdownOptionFormsProps) {
  const isDiscipline = selectedField === 'discipline'

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary" mb={2}>
            Add New Option
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
              <TextField
                size="small"
                label="Display Value"
                fullWidth
                value={newForm.display_value}
                onChange={event =>
                  setNewForm(form => ({
                    ...form,
                    display_value: event.target.value,
                    internal_value: buildInternalValue(event.target.value),
                  }))
                }
              />
              <TextField
                size="small"
                label="Internal Value"
                fullWidth
                value={newForm.internal_value}
                onChange={event => setNewForm(form => ({ ...form, internal_value: event.target.value }))}
              />
              {isDiscipline && (
                <TextField
                  size="small"
                  label="Prefix"
                  fullWidth
                  required
                  value={newForm.prefix}
                  onChange={event => setNewForm(form => ({ ...form, prefix: event.target.value.toUpperCase() }))}
                  placeholder="E"
                  inputProps={{ maxLength: 6 }}
                />
              )}
              <TextField
                size="small"
                label="Sort Order"
                fullWidth
                value={newForm.sort_order}
                onChange={event => setNewForm(form => ({ ...form, sort_order: event.target.value }))}
                placeholder="50"
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newForm.is_active ? 'active' : 'inactive'}
                  label="Status"
                  onChange={event => setNewForm(form => ({ ...form, is_active: event.target.value === 'active' }))}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              size="small"
              label="Description / Usage Note"
              fullWidth
              multiline
              rows={2}
              value={newForm.description}
              onChange={event => setNewForm(form => ({ ...form, description: event.target.value }))}
              placeholder="Optional explanation of where this option should be used."
            />
            <Box display="flex" gap={1}>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onCreate}>
                Save New Option
              </Button>
              <Button variant="outlined" size="small" onClick={onClearNewForm}>
                Clear
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, minHeight: '100%' }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary" mb={2}>
            {editItem ? `Edit: ${editItem.display_value}` : 'Edit Option'}
          </Typography>
          {!editItem ? (
            <Typography variant="body2" color="text.secondary">
              Click the edit icon on any option to edit it here.
            </Typography>
          ) : (
            editForm && (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                  <TextField
                    size="small"
                    label="Display Value"
                    fullWidth
                    value={editForm.display_value}
                    onChange={event => setEditForm(form => (form ? { ...form, display_value: event.target.value } : form))}
                  />
                  <TextField
                    size="small"
                    label="Internal Value"
                    fullWidth
                    value={editForm.internal_value}
                    onChange={event => setEditForm(form => (form ? { ...form, internal_value: event.target.value } : form))}
                  />
                  {isDiscipline && (
                    <TextField
                      size="small"
                      label="Prefix"
                      fullWidth
                      required
                      value={editForm.prefix || ''}
                      onChange={event => setEditForm(form => (form ? { ...form, prefix: event.target.value.toUpperCase() } : form))}
                      inputProps={{ maxLength: 6 }}
                    />
                  )}
                  <TextField
                    size="small"
                    label="Sort Order"
                    fullWidth
                    value={editForm.sort_order}
                    onChange={event =>
                      setEditForm(form => (form ? { ...form, sort_order: Number(event.target.value) || 0 } : form))
                    }
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editForm.is_active ? 'active' : 'inactive'}
                      label="Status"
                      onChange={event => setEditForm(form => (form ? { ...form, is_active: event.target.value === 'active' } : form))}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <TextField
                  size="small"
                  label="Description / Usage Note"
                  fullWidth
                  multiline
                  rows={2}
                  value={editForm.description}
                  onChange={event => setEditForm(form => (form ? { ...form, description: event.target.value } : form))}
                />
                <Box display="flex" gap={1}>
                  <Button variant="contained" color="success" size="small" onClick={onUpdate}>
                    Update Option
                  </Button>
                  <Button variant="outlined" size="small" onClick={onDiscardEdit}>
                    Discard
                  </Button>
                </Box>
              </Box>
            )
          )}
        </Paper>
      </Grid>
    </Grid>
  )
}
